import { ReportInternalType, ReportStatus } from '@/__generated__/graphql';
import { AccessGuard } from '@/components/Analysis/shared/AccessGuard';
import * as state from '@/components/Analysis/state';
import { FilterAndSearchPanel } from '@/components/shared/FilterAndSearchPanel';
import { Snackbar } from '@/components/shared/Snackbar';
import { StructureCheck } from '@/components/shared/StructureCheck';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { abyssColors } from '@/theme/colors';
import { UpgradePromptBackground } from '@/types';
import { PictureAsPdf } from '@mui/icons-material';
import { Box, Button, CircularProgress, Stack, Tab } from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { UpgradePrompt } from '../../shared/UpgradePrompt';
import { useFetchReportGroups } from '../hooks/useFetchReportGroups';
import { ReportGroups } from './ReportGroups';
import { ReportTypeTabs } from './ReportTypeTabs';
import { ReportsList } from './ReportsList';
import { FloatingLine, Panel, ReportTabs, ReportsMainStyles, containerStyles } from './styles';
import { useBulkReportDownload } from './useBulkReportDownload';
import { useReportFilters } from './useReportFilters';
import { SelectedValues } from '../../../shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { LocationTag } from '@/constants';

const reportsTabs = [
  { key: ReportStatus.Draft, label: 'Drafts' },
  { key: ReportStatus.Published, label: 'Open' },
  { key: ReportStatus.Closed, label: 'Closed' },
];

export const ReportsLanding = () => {
  const [selectedReportFilters, setSelectedReportFilters] = useAtom(state.selectedReportFilters);
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState<ReportStatus>(ReportStatus.Draft);
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [selectedReportsMap, setSelectedReportsMap] = useState<Record<string, string>>({});
  const [selectedReportType, setSelectedReportType] = useState<ReportInternalType>(
    ReportInternalType.PointOfInterest
  );

  const structureId = useGetStructureId();
  const navbarHeight = useNavbarHeight();
  const { filters } = useReportFilters();
  const { reportGroups } = useFetchReportGroups(selectedReportType, currentTab, searchTerm);
  const { handleBulkDownload } = useBulkReportDownload({
    selectedReportIds,
    selectedReportsMap,
    setLoading,
  });

  const { shouldShow, content } = snackbarMessage;

  const selectedReportTypeIsComponent = useMemo(() => {
    return selectedReportType === ReportInternalType.Component;
  }, [selectedReportType]);

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const handleReportTypeChange = useCallback(
    (_: React.SyntheticEvent, newValue: ReportInternalType) => {
      setSelectedReportType(newValue);
    },
    [setSelectedReportType]
  );

  const handleTabActiveChange = (event: React.SyntheticEvent, newValue: ReportStatus) => {
    setCurrentTab(newValue);
  };

  const handleSelectedFilterValuesChange = useCallback(
    (selectedValues: SelectedValues) => {
      const filteredSelectedValues = Object.fromEntries(
        Object.entries(selectedValues).filter(([, values]) => values.length > 0)
      );
      setSelectedReportFilters(filteredSelectedValues);
    },
    [setSelectedReportFilters]
  );

  return (
    <StructureCheck structureId={structureId}>
      <AccessGuard structureId={structureId} feature="reports">
        <UpgradePrompt
          feature="reports"
          featureTitle="Reports"
          background={UpgradePromptBackground.Reports}
          paragraphs={[
            'Access enhanced reports with automated site images',
            'View marked location of anomalies',
            'Export reports for streamlined tracking, seamless monitoring, and documentation purposes',
            'Add selected images of anomalies for greater clarity',
          ]}
        >
          <Stack
            direction="row"
            justifyContent="center"
            sx={containerStyles}
            height={`calc(100vh - ${navbarHeight}px)`}
          >
            <Box sx={ReportsMainStyles}>
              <ReportTabs value={currentTab} onChange={handleTabActiveChange}>
                {reportsTabs.map((tab) => (
                  <Tab
                    value={tab.key}
                    key={tab.key}
                    label={tab.label}
                    sx={{ textTransform: 'none' }}
                  />
                ))}
              </ReportTabs>
              <FloatingLine />
              <ReportTypeTabs
                selectedTab={selectedReportType}
                handleChange={handleReportTypeChange}
              />
              <Panel>
                <FilterAndSearchPanel
                  key={`filter-panel-${selectedReportType}`}
                  templateFields={filters}
                  selectedValues={selectedReportFilters}
                  setSelectedValues={setSelectedReportFilters}
                  handleSelectedChange={handleSelectedFilterValuesChange}
                  searchBar
                  searchPlaceholder="Search report"
                  searchTerm={searchTerm}
                  setSearch={setSearchTerm}
                  filterOptions={selectedReportTypeIsComponent}
                  hideStaticFilters
                  location={LocationTag.Reports}
                  endAdornment={
                    selectedReportTypeIsComponent ? (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          minWidth: 'auto',
                          whiteSpace: 'nowrap',
                          height: '3.2rem',
                        }}
                        startIcon={
                          loading ? (
                            <CircularProgress size={20} sx={{ color: abyssColors.primary[500] }} />
                          ) : (
                            <PictureAsPdf sx={{ cursor: 'pointer' }} />
                          )
                        }
                        onClick={handleBulkDownload}
                        disabled={loading || selectedReportIds.length === 0}
                      >
                        Download PDF
                      </Button>
                    ) : null
                  }
                />
              </Panel>
              {reportGroups.length === 0 ? (
                <ReportsList
                  selectedReportType={selectedReportType}
                  status={currentTab}
                  searchTerm={searchTerm}
                  selectedReportIds={selectedReportIds}
                  setSelectedReportIds={setSelectedReportIds}
                  setSelectedReportsMap={setSelectedReportsMap}
                />
              ) : (
                <>
                  {reportGroups.map((group) => (
                    <ReportGroups
                      reportGroup={group}
                      selectedReportType={selectedReportType}
                      status={currentTab}
                      searchTerm={searchTerm}
                      selectedReportIds={selectedReportIds}
                      setSelectedReportIds={setSelectedReportIds}
                      setSelectedReportsMap={setSelectedReportsMap}
                    />
                  ))}
                </>
              )}
              <Snackbar open={shouldShow} onClose={closeSnack}>
                {content}
              </Snackbar>
            </Box>
          </Stack>
        </UpgradePrompt>
      </AccessGuard>
    </StructureCheck>
  );
};
